import { Platform, View } from 'react-native';
import React, { useCallback } from 'react';
import {
  Banner,
  DefaultScreenContainer,
  Footer,
  InsuranceCompanyLogo,
  Text,
} from '~/components';
import useMarisaAutoForm from '~/stores/marisaAutoForm';
import InsuranceLogo from '../QuoteResult/components/InsuranceLogo';
// import StarRating from '~/components/StarRating';
import Divider from '~/scenes/HiredInsurance/components/Divider';
import DetailRow from '../QuoteResult/components/DetailRow';
import { formatMoney } from '~/utils';
import { useCheckout } from '~/stores';
import type { MarisaAuto } from '~/@types/MarisaAuto/form';
import { registerIntentionUseCase } from '~/useCases/PSF/RegisterIntentionUseCase';
import { getRawDocument } from '~/utils/string';
import { isWeb } from '~/utils/device';
import useAppNavigation from '~/hooks/useAppNavigation';
import type { MarisaAutoStepsProps } from '../..';

const QuoteDetails = ({ onContinue }: MarisaAutoStepsProps) => {
  const { goBack } = useAppNavigation();
  const insuranceQuote = useMarisaAutoForm(
    (state) => state.selectedInsuranceQuote
  );
  const insuredDocument = useMarisaAutoForm((state) => state.form?.document);
  const setInsuranceFormData = useCheckout(
    (state) => state.setInsuranceFormData
  );
  const {
    fipeDetails: { fipeValue },
    vehicle,
    brandDescription,
  } = useMarisaAutoForm((state) => state.form!);

  const onFinish = useCallback(() => {
    setInsuranceFormData({ selectedSimulation: insuranceQuote });

    registerIntentionUseCase({
      certificateCode: insuranceQuote.numeroCotacao,
      document: getRawDocument(insuredDocument ?? ''),
      insuranceCompanyCode: insuranceQuote.seguradora,
      insuranceCompanyName: insuranceQuote.empresaDescricao,
      type: 'AUTO',
    });

    onContinue();
  }, [insuranceQuote, insuredDocument, onContinue, setInsuranceFormData]);

  return (
    <DefaultScreenContainer>
      <View className="flex-1 container mx-auto xl:flex-row xl:pt-12 xl:pb-24">
        <Banner type="marisa-auto" />
        <View className="flex-1 mb-6">
          <View className="px-6">
            <View className="flex-row mt-6 items-center">
              <View className="flex-col justify-center items-center">
                <InsuranceLogo refLogo={insuranceQuote.empresaRefLogo} />
                {/* <StarRating
                valueRating={parseInt(insuranceQuote.fatorFipe, 10)}
              /> */}
              </View>
              <Text className="font-medium ml-4 flex-1">
                {insuranceQuote.nome}
              </Text>
            </View>
            <Divider />
            <Text className="flex-grow text-xs font-medium text-primary-600 my-2">
              {`${brandDescription} - ${vehicle.modelDescription} ${vehicle.year}/${vehicle.modelYear}`}
            </Text>
            <DetailRow
              title="Valor da tabela FIPE:"
              value={`R$ ${formatMoney(fipeValue)}`}
            />
            {insuranceQuote.coberturas.map((element, index) => (
              <DetailRow
                key={index}
                title={element.nome}
                value={getValueRow(element)}
              />
            ))}
            <Divider />
            <Text className="text-[11px] flex-grow text-gray-400 mb-8">
              *O valor da proposta desta cotação é válido até{' '}
              {getOfferValidity(insuranceQuote.validadeOferta)}.
            </Text>

            <View
              className={`rounded-md border border-primary-600 p-4 shadow bg-light-100 ${
                Platform.OS === 'android' ? 'shadow-black/40' : ''
              }`}
            >
              <View className="flex-row items-center justify-between mb-2">
                <Text className="font-medium text-sm text-gray-700 flex-1 mr-2">
                  {insuranceQuote.nome}
                </Text>

                <InsuranceCompanyLogo name={insuranceQuote.empresaRefLogo} />
              </View>

              <View className="flex-row items-center justify-between mb-1">
                <Text className="text-gray-400 text-xs">
                  Valor da Franquia:
                </Text>
                <Text className="font-medium text-xs text-primary-600">
                  {formatMoney(insuranceQuote.valorFranquia, true)}
                </Text>
              </View>
              <View className="flex-row items-center justify-between">
                <Text className="text-gray-400 text-xs">
                  Duração da apólice:
                </Text>
                <Text className="font-medium text-xs text-primary-600">
                  1 ano
                </Text>
              </View>

              <View className="h-[1px] bg-gray-100 my-2" />
              <Text className="text-gray-600  text-[10px] mr-1">
                Parcelas à partir de
                <Text className="text-gray-600 text-[10px] font-medium">
                  {` R$${formatMoney(
                    insuranceQuote.bestPaymentOption.installmentValue
                  )}* `}
                </Text>
                {`no ${insuranceQuote.bestPaymentOption.description}`}
              </Text>
            </View>
          </View>
          {isWeb && (
            <Footer
              className="px-6 mt-4"
              backButtonLabel="VOLTAR"
              buttonLabel="COMPRAR AGORA"
              onBack={goBack}
              onSubmit={onFinish}
            />
          )}
        </View>
        {!isWeb && (
          <Footer
            className="px-6 mt-2"
            backButtonLabel="VOLTAR"
            buttonLabel="COMPRAR AGORA"
            onBack={goBack}
            onSubmit={onFinish}
          />
        )}
      </View>
    </DefaultScreenContainer>
  );
};

function getValueRow(coverage: MarisaAuto.Coverage): string {
  if (coverage.valor) {
    const text = coverage.texto?.includes('%')
      ? coverage.texto
      : ` ${coverage.texto}`;
    const value =
      coverage.unidade && coverage.unidade.includes('R$')
        ? formatMoney(coverage.valor)
        : coverage.valor;
    const unity = coverage.unidade ? coverage.unidade : '';

    return `${unity}${value}${text}`;
  } else {
    return 'Não possui';
  }
}

function getOfferValidity(data: string): string {
  const [year, month, day] = data.split('-');

  return `${day}/${month}/${year}`;
}

export default QuoteDetails;
